const STORAGE_KEY = "driverPendingTrackingEvents";

function readAll() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function writeAll(all) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

const actionKey = (action) => `${action.eventType}:${action.placeKey}`;

export function getPendingEvents(scopeKey) {
  return readAll()[scopeKey] || [];
}

export function getAllScopeKeys() {
  return Object.keys(readAll());
}

export function queueEvent(scopeKey, action) {
  const all = readAll();
  const list = all[scopeKey] || [];
  all[scopeKey] = [...list.filter((item) => actionKey(item) !== actionKey(action)), action];
  writeAll(all);
}

export function updatePendingEvent(scopeKey, action) {
  const all = readAll();
  const list = all[scopeKey] || [];
  all[scopeKey] = list.map((item) => (actionKey(item) === actionKey(action) ? { ...item, ...action } : item));
  writeAll(all);
}

export function removePendingEvent(scopeKey, eventType, placeKey) {
  const all = readAll();
  const list = all[scopeKey] || [];
  const next = list.filter((item) => actionKey(item) !== `${eventType}:${placeKey}`);
  if (next.length) {
    all[scopeKey] = next;
  } else {
    delete all[scopeKey];
  }
  writeAll(all);
}

// Turns a queued action into the same shape as a server TrackingEvent so it can be
// merged into trip detail state before it has actually synced.
export function toOptimisticEvent(action) {
  return {
    eventType: action.eventType,
    placeKey: action.placeKey,
    placeLabel: action.placeLabel,
    capturedAtIst: action.clientCapturedAt,
    latitude: action.latitude,
    longitude: action.longitude,
    accuracy: action.accuracy,
    gpsProofType: action.gpsProofType,
    gpsCapturedAtIst: action.gpsCapturedAt,
    odometerReading: action.odometerReading,
    pending: true,
  };
}

// Attempts to send every queued action for a scope via sendFn(action). Successfully sent
// actions are removed from the queue and reported via onSynced(event); failures are left
// queued for the next sync attempt.
export async function flushPendingEvents(scopeKey, sendFn, onSynced, options = {}) {
  const items = getPendingEvents(scopeKey);
  for (const item of items) {
    try {
      const action = await enrichGpsIfNeeded(scopeKey, item, options.captureGpsProof);
      const event = await sendFn(action);
      removePendingEvent(scopeKey, action.eventType, action.placeKey);
      onSynced(event);
    } catch {
      // Still offline (or server unreachable) - leave queued for the next attempt.
    }
  }
}

async function enrichGpsIfNeeded(scopeKey, item, captureGpsProof) {
  if (item.latitude != null && item.longitude != null) return item;
  if (typeof captureGpsProof !== "function") {
    throw new Error("GPS location is required before syncing this checkbox");
  }
  const proof = await captureGpsProof("DELAYED");
  if (proof.latitude == null || proof.longitude == null) {
    throw new Error("GPS location is required before syncing this checkbox");
  }
  const action = {
    ...item,
    ...proof,
    gpsProofType: "DELAYED",
  };
  updatePendingEvent(scopeKey, action);
  return action;
}
